/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import * as React from "react";

import * as API from "../../../../api/manageCatalog";
import {
  DenominationButtonPropTypes,
  DenominationAdminState,
} from "../lib/types";
import {
  StyledDenominationButtonGreen,
  StyledDenominationButtonLightGray,
  StyledDenominationButtonRed,
  StyledParagraphNoMargin,
} from "../lib/styles";
import { datadogRum } from "@datadog/browser-rum";
import { formatCurrency } from "../../../../utils";
import {
  getDenominationState,
  getErrorMessage,
  getInactiveReasons,
} from "../lib/helpers";

const DenominationButton = ({
  brandName,
  currency,
  denomination,
  disabled,
  updateToastMessage,
}: DenominationButtonPropTypes): React.ReactElement => {
  const [state, setState] = React.useState<DenominationAdminState>(
    getDenominationState(denomination.inactiveReasons)
  );
  const [loading, setLoading] = React.useState<boolean>(false);

  const formattedCurrency = formatCurrency(
    denomination.priceInSubunits / 10 ** currency.exponent,
    currency.iso4217Code
  );

  const handleClick = async (): Promise<void> => {
    try {
      setLoading(true);
      const nextState = state === "disabled" ? "enabled" : "disabled";
      const updatedDenomination = {
        bufferable: denomination.bufferable,
        external_identifier: denomination.externalIdentifier,
        id: denomination.id,
        inactive_reasons: getInactiveReasons(
          nextState,
          denomination.inactiveReasons
        ),
        item_id: denomination.itemId,
        legacy_id: denomination.legacyId,
        price_in_subunits: denomination.priceInSubunits,
      };

      const response = await API.updateDenomination(updatedDenomination);

      // NOTE: updating the state here allows the new state to show without reloading the entire table
      setState(getDenominationState(response.data.data.inactiveReasons));
      setLoading(false);
    } catch (error) {
      datadogRum.addError(error);
      const errorMessage = getErrorMessage(error);
      const message = (
        <>
          <p>
            An error occured while trying to change the {formattedCurrency}{" "}
            denomination state for {brandName}.
          </p>
          <StyledParagraphNoMargin>{errorMessage}</StyledParagraphNoMargin>
        </>
      );

      updateToastMessage({
        isOpen: true,
        message,
        type: "error",
      });
      setLoading(false);
    }
  };

  let StyledButtonComponent;
  if (disabled) {
    StyledButtonComponent = StyledDenominationButtonLightGray;
  } else if (state === "enabled") {
    StyledButtonComponent = StyledDenominationButtonGreen;
  } else {
    StyledButtonComponent = StyledDenominationButtonRed;
  }

  return (
    <StyledButtonComponent
      data-testid={`denomination-${denomination.id}-${state}`}
      disabled={disabled || loading}
      onClick={handleClick}
    >
      {formattedCurrency}
    </StyledButtonComponent>
  );
};

export default DenominationButton;
